import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  DecoratedCompanyInvestor,
  DecoratedInvestorCompany,
  UndecoratedCompanyInvestor,
} from '@flemingmetrics/api-client';
import { deleteInvestor } from '@/store/slices/investorsSlice';
import { filterByValue, mergeWithoutDuplicates } from '@/utils/arrays';
import { buildCompositeKey } from '@/store/utils';

type CompanyInvestorsState = {
  byId: Record<string, UndecoratedCompanyInvestor>;
  byCompanyId: Record<string, string[]>;
  byInvestorId: Record<string, string[]>;
};

const initialState: CompanyInvestorsState = {
  byId: {},
  byCompanyId: {},
  byInvestorId: {},
};

export const companyInvestorsSlice = createSlice({
  name: 'companyInvestors',
  initialState,
  reducers: {
    addCompanyInvestor: (
      state,
      action: PayloadAction<DecoratedCompanyInvestor>
    ) => {
      return getNextState(state, [action.payload]);
    },
    setCompanyInvestors: (
      state,
      action: PayloadAction<DecoratedCompanyInvestor[]>
    ) => {
      return getNextState(state, action.payload);
    },
    setInvestorCompanies: (
      state,
      action: PayloadAction<DecoratedInvestorCompany[]>
    ) => {
      return getNextState(state, action.payload);
    },
    deleteCompanyInvestor: (
      state,
      action: PayloadAction<{
        companyId: number;
        investorId: number;
        investmentIds: number[];
      }>
    ) => {
      const { companyId, investorId } = action.payload;
      const key = buildCompanyInvestorKey(companyId, investorId);
      const byId = { ...state.byId };
      const byCompanyId = { ...state.byCompanyId };
      const byInvestorId = { ...state.byInvestorId };
      byCompanyId[companyId] = filterByValue(byCompanyId[companyId], key);
      byInvestorId[investorId] = filterByValue(byInvestorId[investorId], key);
      delete byId[key];
      return { byId, byCompanyId, byInvestorId };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteInvestor, (state, action) => {
        const { investorId } = action.payload;
        const keys = state.byInvestorId[investorId];
        if (!keys) return state;
        const byId = { ...state.byId };
        const byCompanyId = { ...state.byCompanyId };
        const byInvestorId = { ...state.byInvestorId };
        keys.forEach((key) => {
          if (!byId[key]) return;
          const { companyId } = byId[key];
          byCompanyId[companyId] = filterByValue(byCompanyId[companyId], key);
          delete byId[key];
        });
        delete byInvestorId[investorId];
        return { byId, byCompanyId, byInvestorId };
      })
      .addDefaultCase(() => {});
  },
});

function formatCompanyInvestor(
  entry: DecoratedCompanyInvestor | DecoratedInvestorCompany
): UndecoratedCompanyInvestor {
  if ('company' in entry) {
    const { company, ...companyInvestor } = entry;
    return companyInvestor;
  } else {
    const { investor, ...companyInvestor } = entry;
    return companyInvestor;
  }
}

function getNextState(
  state: CompanyInvestorsState,
  companyInvestors: (DecoratedCompanyInvestor | DecoratedInvestorCompany)[]
) {
  const byId = { ...state.byId };
  const byCompanyId = { ...state.byCompanyId };
  const byInvestorId = { ...state.byInvestorId };
  companyInvestors.forEach((ci) => {
    const { companyId, investorId } = ci;
    const key = buildCompanyInvestorKey(companyId, investorId);
    byId[key] = formatCompanyInvestor(ci);
    if (!byCompanyId[companyId]) byCompanyId[companyId] = [];
    if (!byInvestorId[investorId]) byInvestorId[investorId] = [];
    byCompanyId[companyId] = mergeWithoutDuplicates(byCompanyId[companyId], [
      key,
    ]);
    byInvestorId[investorId] = mergeWithoutDuplicates(
      byInvestorId[investorId],
      [key]
    );
  });
  return { byId, byCompanyId, byInvestorId };
}

export function buildCompanyInvestorKey(companyId: number, investorId: number) {
  return buildCompositeKey(companyId, investorId);
}

export const {
  addCompanyInvestor,
  setCompanyInvestors,
  setInvestorCompanies,
  deleteCompanyInvestor,
} = companyInvestorsSlice.actions;

export default companyInvestorsSlice.reducer;
